import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowUpIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownIcon from "@mui/icons-material/ArrowDownward";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import WarningIcon from "@mui/icons-material/WarningAmberOutlined";
import { useMemo, useState } from "react";
import { tokens } from "../theme";

/**
 * An ordered list of ids picked from a catalogue (BE-28).
 *
 * Used for `RoomType.carousels[]` and `RoomType.pages[]` — the SELECTION layer.
 *
 * ## Order is array position, never a `position` field
 *
 * A number per item can hold gaps, duplicates and holes a delete left behind,
 * and then two people disagree about what "3" means. The array is the order,
 * full stop, which also makes the reorder control trivially correct.
 *
 * ## Arrows, not drag
 *
 * Upstream uses a drag handle. Arrows are used here because they are the same
 * operation with a keyboard, a screen reader and a touch screen, they need no
 * library, and reordering a rail is a rare, deliberate act rather than
 * something worth optimising the mouse path for. Each button says where the row
 * is going, so the action is announced rather than felt.
 *
 * ## An unknown id is SHOWN, not hidden
 *
 * During the parallel period an operator legitimately holds an id whose row is
 * still only in the portal, and a room type copied from the portal can arrive
 * with an id that no longer resolves at all. Dropping it from this list would
 * silently edit their line-up; showing it as unresolved lets them decide.
 */

export interface OrderedSelectionOption {
  id: string;
  label: string;
}

export interface OrderedSelectionProps {
  label: string;
  hint?: string;
  /** The picked ids, in display order. */
  value: string[];
  /** Everything pickable. An id in `value` that is absent here is unresolved. */
  options: OrderedSelectionOption[];
  onChange: (next: string[]) => void;
  testId?: string;
}

export default function OrderedSelection({
  label,
  hint,
  value,
  options,
  onChange,
  testId,
}: OrderedSelectionProps) {
  const [adding, setAdding] = useState(false);

  const byId = useMemo(
    () => new Map(options.map((o) => [o.id, o.label])),
    [options]
  );
  // Duplicates are not filtered out of `value` — that is the operator's data
  // and quietly de-duplicating it would change a count they are looking at.
  // They ARE excluded from the add list, because adding a second copy is never
  // the intent behind picking from a menu.
  const picked = useMemo(() => new Set(value), [value]);
  const addable = options.filter((o) => !picked.has(o.id));

  const move = (index: number, delta: number) => {
    const next = [...value];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  return (
    <Box data-testid={testId}>
      <Typography sx={{ fontWeight: 600, fontSize: 13.5 }}>{label}</Typography>
      {hint && (
        <Typography sx={{ color: tokens.inkFaint, fontSize: 12, mb: 1 }}>
          {hint}
        </Typography>
      )}

      <Stack spacing={0.5} sx={{ mb: 1 }}>
        {value.length === 0 && (
          <Typography sx={{ color: tokens.inkFaint, fontSize: 12.5, py: 1 }}>
            Nothing selected yet.
          </Typography>
        )}
        {value.map((id, index) => {
          const known = byId.has(id);
          return (
            <Stack
              key={`${id}-${index}`}
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                border: `1px solid ${tokens.divider}`,
                borderRadius: 1.5,
                px: 1.25,
                py: 0.5,
              }}
            >
              <Typography
                sx={{
                  color: tokens.inkFaint,
                  fontSize: 12,
                  width: 20,
                  flexShrink: 0,
                }}
              >
                {index + 1}
              </Typography>
              <Typography sx={{ flex: 1, fontSize: 13.5 }} noWrap title={id}>
                {byId.get(id) ?? id}
              </Typography>
              {!known && (
                <Tooltip title="This id does not resolve to anything here. It may still only exist on the legacy portal, or it may point at something that was deleted. Nothing is drawn for it.">
                  <WarningIcon sx={{ fontSize: 16, color: tokens.warning }} />
                </Tooltip>
              )}
              <IconButton
                size="small"
                aria-label={`Move ${byId.get(id) ?? id} up`}
                disabled={index === 0}
                onClick={() => move(index, -1)}
              >
                <ArrowUpIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                aria-label={`Move ${byId.get(id) ?? id} down`}
                disabled={index === value.length - 1}
                onClick={() => move(index, 1)}
              >
                <ArrowDownIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                aria-label={`Remove ${byId.get(id) ?? id}`}
                onClick={() => onChange(value.filter((_, i) => i !== index))}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          );
        })}
      </Stack>

      {adding ? (
        <TextField
          select
          size="small"
          fullWidth
          label={`Add to ${label.toLowerCase()}`}
          value=""
          onChange={(e) => {
            onChange([...value, e.target.value]);
            setAdding(false);
          }}
        >
          {addable.map((o) => (
            <MenuItem key={o.id} value={o.id}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
      ) : (
        <Button
          size="small"
          disabled={addable.length === 0}
          onClick={() => setAdding(true)}
        >
          Add
        </Button>
      )}
    </Box>
  );
}
